import type { DealVisibility } from '@makemydeal/dr-shared-bff-types';

import { Actions } from '../actionTypes/actionTypes';
import type { UpdateDealSummaryMeta } from './types';

/**
 * Visibility a deal carries once it has been sent to the desk: it needs a manager to look
 * at it, hasn't been approved yet, and is still a draft.
 *
 * Duplicate of the payload the Deal Summary "Send Request" button builds inline in
 * activities-request-proposal — to be revisited so both entry points share one constant.
 */
export const SEND_TO_DESK_VISIBILITY: DealVisibility = {
    needsManagerApproval: true,
    approvedByManager: false
};

/**
 * Sets the visibility flags on the active deal. Handled by `activeDealReducer` and read
 * back out of state when the deal save builds its payload.
 */
export const updateActiveDealVisibilityStatus = (visibility: DealVisibility) => ({
    type: Actions.UPDATE_ACTIVE_DEAL_VISIBILITY_STATUS,
    payload: { visibility }
});

/**
 * Sets whether the buyer has accepted the active deal. Handled by `activeDealReducer` and read
 * back out of state when the deal save builds its payload.
 */
export const setDealAcceptedByBuyer = (acceptedByBuyer: boolean) => ({
    type: Actions.SET_DEAL_ACCEPTED_BY_BUYER,
    payload: { acceptedByBuyer }
});

/**
 * Folds the deal a participants save returned into the active deal, so consumers reading the deal
 * node render the saved values instead of the pre-edit ones.
 */
export const updateActiveDealFromParticipantsSave = (dxgDeal: Record<string, any>, applicationState: string) => ({
    type: Actions.UPDATE_ACTIVE_DEAL_FROM_PARTICIPANTS_SAVE,
    payload: { dxgDeal, applicationState }
});

/**
 * Saves the active deal (`PUT /api/deals`) via `MiddlewareManager`. Dispatch it once the
 * state the save should carry is already in the store — the payload is built from state,
 * not from this action.
 *
 * @param meta forwarded onto `UPDATE_DEAL_SUCCESS` so the caller's intent survives the save.
 * `sentToDesk` is how the confirmation snackbar is attributed: every save in the app fires
 * the same success action, so only the one flagged here reports "Deal sent to the desk".
 */
export const updateDealSummary = (meta?: UpdateDealSummaryMeta) => ({
    type: Actions.UPDATE_DEAL_SUMMARY,
    meta
});

/**
 * Sets the note the deal payload sends.
 *
 * Duplicate of activities-request-proposal's `updateNotes` — to be revisited: the notes
 * themselves live in that activity's slice, so this only works because the action type
 * string matches.
 */
export const updateNotes = (notes: string) => ({
    type: Actions.REQUEST_PROPOSAL_UPDATE_NOTES,
    payload: notes
});
